import React from 'react';
import { T } from '../constants';
import SystemBadge from '../components/badges/SystemBadge';

// ─────────────────────────────────────────────────────────────────────
// SequencingMap — how trades are claimed across the desk: four slots
// (asset × session), one position each, books firing in waves with a
// downstream book deferring to whoever already holds its slot. Built from
// the same dashboard primitives (.stat-tile, .badge, SystemBadge, surface
// panels, Roboto-Mono numerics) so it reads as part of the portfolio, not a
// bolt-on. Source: live strategy_registry (waves + reads_upstream + fallbacks).
// ─────────────────────────────────────────────────────────────────────
const NQ = '#2dd4bf';        // NQ asset accent
const ES = '#8aa0ff';        // ES asset accent
const DEFER = '#f0b90b';     // --tv-gold — defers / reads upstream
const MIRROR = '#9c6ef5';    // --tv-purple — mirrors another book
const MONO = "'Roboto Mono', ui-monospace, monospace";

const DIR_COLOR = { long: T.bull, short: T.bear, dual: T.textDim, mirror: MIRROR };

const SESSIONS = [
  {
    title: 'Regular Trading Hours', clock: '09:30 → 16:00 ET · flat at close', flow: 'wave = time ↓',
    lanes: [
      {
        asset: 'NQ', accent: NQ, slot: 'RTH slot',
        books: [
          { wave: 'W1', sys: 'ANYANWU', leader: true, time: '09:30–09:35',
            dirs: [['LONG / SHORT', 'dual']], stop: '−125bp', note: 'grade-sized, dual books at the open',
            gate: 'own NQ grade (nq_ret1/nq_slope) · first claim on the slot' },
          { wave: 'W3', sys: 'JUYI', time: 'intraday ~10:2x',
            dirs: [['SHORT', 'short'], ['LONG', 'long']], stop: '−175bp', note: 'confluence reversal (4+/5+ algos)',
            defer: 'defers to ANYANWU · + own single-slot guard' },
          { wave: 'W3', sys: 'ISINMI', time: 'after ANYANWU · fb 09:45',
            dirs: [['MIRRORS ES ARM', 'mirror']], stop: '−200bp', note: 'SIRIUS→short · RIGEL→long',
            defer: 'defers to ANYANWU, JUYI' },
        ],
      },
      {
        asset: 'ES', accent: ES, slot: 'RTH slot',
        books: [
          { wave: 'W1', sys: 'SIRIUS', leader: true, time: '09:35',
            dirs: [['SHORT', 'short']], stop: '−150bp', note: 'grade-A momentum',
            gate: 'first claim on the ES slot' },
          { wave: 'W2', sys: 'RIGEL', time: 'after SIRIUS · fb 09:40',
            dirs: [['LONG', 'long']], stop: '−200bp', note: 'trend, A-tier sizes 2×',
            defer: 'defers to SIRIUS' },
          { wave: 'W3', sys: 'MARS', time: 'after SIRIUS+RIGEL · fb 09:46',
            dirs: [['MIRRORS ANYANWU', 'mirror']], stop: '−75bp', note: 'grade-D low-vol refuse',
            defer: 'defers to SIRIUS, RIGEL · reads ANYANWU for direction' },
        ],
      },
    ],
  },
  {
    title: 'Overnight', clock: '16:00 → 09:30 ET next · exit at the open', flow: 'eval at the close ↓',
    lanes: [
      {
        asset: 'NQ', accent: NQ, slot: 'Overnight slot',
        books: [
          { wave: '16:05', sys: 'OSUPA', leader: true, time: 'exit 09:30',
            dirs: [['LONG', 'long']], stop: '−100bp', note: 'above a rising SMA100, composite grade',
            gate: 'the overnight anchor — IYANU reads it' },
          { wave: '16:20', sys: 'IYANU', time: 'after OSUPA · exit 09:30',
            dirs: [['LONG', 'long']], stop: '−150bp', note: 'dip-buy the uptrend (≤400bp, ret5<−100bp)',
            defer: 'reads OSUPA (fires when OSUPA passes)' },
          { wave: '16:05', sys: 'ISALE', time: 'exit 09:30',
            dirs: [['LONG', 'long']], stop: '−175bp', note: 'simple single-exit overnight',
            indep: 'independent book — no upstream' },
        ],
      },
      {
        asset: 'ES', accent: ES, slot: 'Overnight slot',
        books: [
          { wave: '16:06', sys: 'SATURN', leader: true, time: 'exit 09:30',
            dirs: [['LONG', 'long']], stop: '−100bp', note: 'buy ES when NQ sits below its SMA100',
            gate: 'regime-opposite twin of VENUS' },
          { wave: '16:15', sys: 'VENUS', time: 'after SATURN · exit 09:30',
            dirs: [['LONG', 'long']], stop: '−150bp', note: 'pullback within an NQ uptrend',
            defer: 'defers to SATURN (fires only when SATURN skips)' },
        ],
      },
    ],
  },
];

const TILES = [
  { label: 'Slots', value: '4', sub: 'asset × session', color: NQ },
  { label: 'Live books', value: '11', sub: 'registry-gated', color: T.blue },
  { label: 'RTH', value: '6', sub: '2 slots · NQ + ES', color: T.bull },
  { label: 'Overnight', value: '5', sub: '2 slots · NQ + ES', color: DEFER },
];

function DirBadge({ label, kind }) {
  const c = DIR_COLOR[kind] || T.textDim;
  return (
    <span className="badge" style={{ background: `${c}18`, border: `1px solid ${c}45`, color: c, fontSize: 9 }}>
      {label}
    </span>
  );
}

function BookRow({ b, accent, first }) {
  return (
    <div style={{
      padding: '11px 14px', display: 'flex', flexDirection: 'column', gap: 7,
      borderTop: first ? 'none' : `1px dashed ${T.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: MONO, fontSize: 9, color: T.textMuted,
          border: `1px solid ${T.border}`, borderRadius: 3, padding: '1px 5px',
        }}>{b.wave}</span>
        <SystemBadge systemId={b.sys} />
        {b.leader && (
          <span className="badge" style={{
            background: accent, color: '#0b0e14', fontSize: 8.5, letterSpacing: '0.1em',
          }}>SLOT LEADER</span>
        )}
        <span className="mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: T.textMuted }}>{b.time}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5, color: T.textMuted }}>
        {b.dirs.map(([label, kind]) => <DirBadge key={label} label={label} kind={kind} />)}
        <span style={{ opacity: 0.45 }}>·</span>
        <span className="mono" style={{ color: T.bear, fontSize: 11 }}>stop {b.stop}</span>
        <span style={{ opacity: 0.45 }}>·</span>
        <span>{b.note}</span>
      </div>

      {b.gate && <div className="mono" style={{ fontSize: 10.5, color: T.textDim }}>{b.gate}</div>}
      {b.defer && (
        <div className="mono" style={{ fontSize: 11, color: DEFER }}>
          <span style={{ opacity: 0.85 }}>⤷ </span>{b.defer}
        </div>
      )}
      {b.indep && <div className="mono" style={{ fontSize: 10.5, color: T.textDim }}>{b.indep}</div>}
    </div>
  );
}

function SlotPanel({ lane }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(20,30,55,0.04)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <span className="badge mono" style={{
          background: `${lane.accent}18`, border: `1px solid ${lane.accent}45`, color: lane.accent, fontSize: 9,
        }}>{lane.asset}</span>
        <span className="stat-label" style={{ margin: 0 }}>{lane.slot}</span>
        <span className="hist-count" style={{ marginLeft: 'auto' }}>one position</span>
      </div>
      <div>
        {lane.books.map((b, i) => <BookRow key={b.sys} b={b} accent={lane.accent} first={i === 0} />)}
      </div>
    </div>
  );
}

export default function SequencingMap() {
  return (
    <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', background: T.bg }}>
      <div className="page-container" style={{ padding: '16px 16px 48px', color: T.text }}>

        {/* header */}
        <div className="stat-label" style={{ margin: 0, color: NQ }}>Registry Sequencing</div>
        <h1 style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.01em', margin: '6px 0 8px', color: T.text }}>
          How trades come now
        </h1>
        <p style={{ color: T.textMuted, fontSize: 12.5, lineHeight: 1.55, maxWidth: '84ch', margin: 0 }}>
          Eleven live books, but never eleven positions. The desk is carved into <b style={{ color: T.text }}>four slots</b>{' '}
          — asset&nbsp;×&nbsp;session — and each holds <b style={{ color: T.text }}>one position at a time</b>. Books fire in{' '}
          <b style={{ color: T.text }}>waves</b>; a later book <b style={{ color: T.text }}>defers</b> to whoever already
          holds its slot. Every book is <b style={{ color: T.text }}>registry-gated</b> — turning one off is a DB flip, not a deploy.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 18px', margin: '10px 0 16px', fontFamily: MONO, fontSize: 11, color: T.textMuted }}>
          <span>• 4 slots · never cross-block</span>
          <span>• one position / slot</span>
          <span>• downstream defers upstream</span>
          <span>• enable = DB flip, fail-open</span>
        </div>

        {/* KPI tiles */}
        <div className="stats-row">
          {TILES.map(t => (
            <div key={t.label} className="stat-tile">
              <div className="stat-label">{t.label}</div>
              <div className="stat-value" style={{ color: t.color }}>{t.value}</div>
              <div className="stat-sub">{t.sub}</div>
            </div>
          ))}
        </div>

        {/* sessions */}
        {SESSIONS.map(s => (
          <section key={s.title} style={{ marginTop: 22 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
              paddingBottom: 9, marginBottom: 12, borderBottom: `1px solid ${T.border}`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.text }}>
                {s.title}
              </span>
              <span className="mono" style={{ fontSize: 11, color: DEFER }}>{s.clock}</span>
              <span className="mono" style={{ marginLeft: 'auto', fontSize: 10, color: T.textDim }}>{s.flow}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 12 }}>
              {s.lanes.map(lane => <SlotPanel key={lane.asset} lane={lane} />)}
            </div>
          </section>
        ))}

        {/* legend */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: 22, paddingTop: 14,
          borderTop: `1px solid ${T.border}`, fontFamily: MONO, fontSize: 11, color: T.textMuted,
        }}>
          {[['NQ slot', NQ], ['ES slot', ES], ['long', T.bull], ['short', T.bear], ['mirrors a book', MIRROR], ['⤷ defers / reads', DEFER]].map(([lbl, c]) => (
            <span key={lbl} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />
              {lbl}
            </span>
          ))}
        </div>
        <div className="mono" style={{ marginTop: 14, color: T.textDim, fontSize: 10.5 }}>
          Source: strategy_registry (live) · waves + reads_upstream + fallback times · one position per asset×session slot.
        </div>

      </div>
    </div>
  );
}
